/**
 * SITE-GEOMETRY.UTILS
 * Manipulation des géométries de lieux (surfaces et linéaires).
 *
 * Une surface peut être stockée en deux formats :
 *  - Ancien : SitePoint[]            => un anneau plat (zone simple)
 *  - Nouveau : SiteMultiPolygon      => SiteRing[][] (plusieurs polygones, chacun avec trous)
 *
 * On normalise via toMultiPolygon ou toMultiLine selon geometryType.
 * Les coordonnées sont normalisées dans [0,1] (relatives à l'image du plan).
 */
import { difference, MultiPolygon as PcMultiPolygon, Ring as PcRing } from "polygon-clipping"
import {
  SiteGeometry,
  SiteGeometryType,
  SiteLine,
  SiteMultiLine,
  SiteMultiPolygon,
  SitePoint,
  SiteRing
} from "@/entities/site/model/site.types"

// Mise à l'échelle des coords [0,1] avant clipping (précision de polygon-clipping)
const SCALE = 1e5
// Aire minimale (en coords normalisées) en-dessous de laquelle on ignore un éclat
export const MIN_AREA = 1e-7

/** Aire signée d'un anneau (formule du lacet). */
function polygonArea(ring: SiteRing): number {
  let area = 0
  const n = ring.length
  for (let i = 0; i < n; i++) {
    const p1 = ring[i]
    const p2 = ring[(i + 1) % n]
    area += p1.x * p2.y - p2.x * p1.y
  }
  return area / 2
}

/** Aire totale d'une géométrie (extérieurs − trous), en valeur absolue. */
export function totalArea(geometry?: SiteGeometry | null): number {
  const mp = toMultiPolygon(geometry)
  let area = 0
  for (const polygon of mp) {
    polygon.forEach((ring, index) => {
      const ringArea = Math.abs(polygonArea(ring))
      area += index === 0 ? ringArea : -ringArea // index 0 = extérieur, suivants = trous
    })
  }
  return area
}

/** Détecte si la valeur stockée est déjà un multi-polygone (vs un anneau plat). */
function isMultiPolygon(geometry: SiteGeometry): geometry is SiteMultiPolygon {
  // Multi-polygone => premier élément = polygone = tableau d'anneaux => Array
  // Anneau plat => premier élément = point => { x, y }
  return Array.isArray(geometry) && geometry.length > 0 && Array.isArray(geometry[0])
}

/** Normalise n'importe quelle géométrie surfacique vers un SiteMultiPolygon. */
export function toMultiPolygon(geometry?: SiteGeometry | null): SiteMultiPolygon {
  if (!geometry || geometry.length === 0) return []
  if (isMultiPolygon(geometry)) return geometry
  // Ancien format : un seul polygone, un seul anneau extérieur
  return [[geometry as SiteRing]]
}

/** Plus grand anneau extérieur (pour édition, centroïde, pins). */
function getLargestOuterRing(geometry?: SiteGeometry | null): SitePoint[] {
  const mp = toMultiPolygon(geometry)
  let best: SiteRing = []
  let bestArea = -Infinity
  for (const polygon of mp) {
    const outer = polygon[0] ?? []
    const area = Math.abs(polygonArea(outer))
    if (area > bestArea) {
      bestArea = area
      best = outer
    }
  }
  return best
}

/** Construit l'attribut `d` d'un <path> SVG (anneaux extérieurs + trous). */
function multiPolygonToSvgPath(geometry?: SiteGeometry | null): string {
  const mp = toMultiPolygon(geometry)
  const ringToPath = (ring: SiteRing) =>
    ring.length ? "M" + ring.map((p) => `${p.x},${p.y}`).join("L") + "Z" : ""
  return mp
    .flatMap((polygon) => polygon.map(ringToPath))
    .filter(Boolean)
    .join(" ")
}

/** Normalise une géométrie linéaire vers une liste de lignes. */
export function toMultiLine(geometry?: SiteGeometry | null): SiteMultiLine {
  if (!geometry || geometry.length === 0) return []
  return Array.isArray(geometry[0]) ? (geometry as SiteMultiLine) : [geometry as SiteLine]
}

/** Construit un chemin SVG ouvert pour une ligne ou un multi-ligne. */
function multiLineToSvgPath(geometry?: SiteGeometry | null): string {
  return toMultiLine(geometry)
    .map((line) => (line.length ? "M" + line.map((p) => `${p.x},${p.y}`).join("L") : ""))
    .filter(Boolean)
    .join(" ")
}

export function geometryToSvgPath(type: SiteGeometryType, geometry?: SiteGeometry | null): string {
  return type === "line" ? multiLineToSvgPath(geometry) : multiPolygonToSvgPath(geometry)
}

/** Chemin principal utilisé par l'éditeur. */
export function getPrimaryGeometryPath(type: SiteGeometryType, geometry?: SiteGeometry | null): SitePoint[] {
  if (type === "polygon") return getLargestOuterRing(geometry)
  return toMultiLine(geometry).reduce<SiteLine>((longest, line) => (line.length > longest.length ? line : longest), [])
}

/** Point situé à mi-distance d'un linéaire. */
export function getLineMidpoint(geometry?: SiteGeometry | null): SitePoint {
  const line = getPrimaryGeometryPath("line", geometry)
  if (!line.length) return { x: 0, y: 0 }
  if (line.length === 1) return line[0]

  const lengths = line.slice(1).map((point, index) =>
    Math.hypot(point.x - line[index].x, point.y - line[index].y)
  )
  const half = lengths.reduce((sum, length) => sum + length, 0) / 2
  let travelled = 0

  for (let index = 0; index < lengths.length; index++) {
    if (travelled + lengths[index] >= half) {
      const ratio = lengths[index] ? (half - travelled) / lengths[index] : 0
      return {
        x: line[index].x + (line[index + 1].x - line[index].x) * ratio,
        y: line[index].y + (line[index + 1].y - line[index].y) * ratio
      }
    }
    travelled += lengths[index]
  }

  return line[line.length - 1]
}

/* ---------- Conversions polygon-clipping (Pair = [number, number]) ---------- */

function ringToPc(ring: SiteRing): PcRing {
  return ring.map((p) => [p.x * SCALE, p.y * SCALE] as [number, number])
}

function multiPolygonToPc(mp: SiteMultiPolygon): PcMultiPolygon {
  return mp.map((polygon) => polygon.map(ringToPc))
}

/** Reconvertit un anneau polygon-clipping (fermé) en SiteRing ouvert. */
function pcRingToRing(ring: PcRing): SiteRing {
  const points = ring.map((pair) => ({ x: pair[0] / SCALE, y: pair[1] / SCALE }))
  // polygon-clipping ferme les anneaux (1er point = dernier) : on retire le doublon
  if (points.length > 1) {
    const first = points[0]
    const last = points[points.length - 1]
    if (first.x === last.x && first.y === last.y) points.pop()
  }
  return points
}

function pcToMultiPolygon(mp: PcMultiPolygon): SiteMultiPolygon {
  return mp
    .map((polygon) => polygon.map(pcRingToRing))
    // Ignore les polygones dont l'anneau extérieur est un éclat négligeable
    .filter((polygon) => polygon[0] && Math.abs(polygonArea(polygon[0])) > MIN_AREA)
}

/**
 * Soustrait `topRing` (zone du dessus) à la géométrie `target`.
 * Renvoie un SiteMultiPolygon (vide si target entièrement recouverte).
 */
export function subtractRing(target: SiteGeometry, topRing: SiteRing): SiteMultiPolygon {
  const targetMp = toMultiPolygon(target)
  if (targetMp.length === 0 || topRing.length < 3) return targetMp

  const result = difference(multiPolygonToPc(targetMp), [ringToPc(topRing)])
  return pcToMultiPolygon(result)
}
